import React, { useRef, useEffect } from 'react';
import { reviews } from '../data';

const ratingBars = [
  { stars: 5, pct: 92 }, { stars: 4, pct: 6 }, { stars: 3, pct: 1.5 }, { stars: 2, pct: 0.5 }, { stars: 1, pct: 0 },
];

export default function Reviews() {
  const trackRef = useRef<HTMLDivElement>(null);

  return (
    <section id="reviews" className="py-24 md:py-32 relative overflow-hidden" style={{background:'#0F1629'}}>
      <div className="orb w-72 h-72 -bottom-10 left-20 opacity-10" style={{background:'rgba(74,144,226,0.5)',animationDelay:'-2s'}} />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16 reveal">
          <span className="section-tag">Student Feedback</span>
          <h2 className="font-playfair font-black text-white mb-4" style={{fontFamily:'Playfair Display,serif',fontSize:'clamp(2rem,3.5vw,3rem)'}}>
            1,500+ <span style={{color:'#C8A84B'}}>Happy Students</span>
          </h2>
        </div>

        {/* Rating summary */}
        <div className="reveal flex flex-col md:flex-row items-center gap-12 justify-center mb-16 p-8 rounded-sm"
          style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(200,168,75,0.12)'}}>
          <div className="text-center">
            <div className="font-black leading-none" style={{fontFamily:'Playfair Display,serif',fontSize:'6rem',color:'#C8A84B'}}>4.9</div>
            <div className="flex justify-center gap-1 my-2">
              {[1,2,3,4,5].map(i => <span key={i} style={{color:'#C8A84B',fontSize:'1.2rem'}}>★</span>)}
            </div>
            <div style={{color:'rgba(212,207,197,0.4)',fontSize:'0.75rem',letterSpacing:'2px',textTransform:'uppercase'}}>Out of 5</div>
          </div>

          <div style={{width:'280px'}}>
            {ratingBars.map(({stars, pct}) => (
              <div key={stars} className="flex items-center gap-3 mb-2">
                <span style={{fontFamily:'Space Mono,monospace',fontSize:'0.7rem',color:'rgba(212,207,197,0.5)',width:'16px'}}>{stars}</span>
                <div className="flex-1 h-[5px] rounded-full overflow-hidden" style={{background:'rgba(255,255,255,0.06)'}}>
                  <div className="h-full rounded-full" style={{width:`${pct}%`,background:'linear-gradient(90deg,#C8A84B,#F0D080)',transition:'width 1.5s ease'}} />
                </div>
                <span style={{fontSize:'0.7rem',color:'rgba(212,207,197,0.4)',width:'32px'}}>{pct}%</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-6">
            {[['1500+', 'Total Reviews'], ['4.9★', 'Avg Rating'], ['24+', 'Years Trusted'], ['400+', 'Graduates']].map(([val, label]) => (
              <div key={label} className="text-center">
                <div className="font-black text-white text-2xl" style={{fontFamily:'Playfair Display,serif',color:'#C8A84B'}}>{val}</div>
                <div style={{color:'rgba(212,207,197,0.4)',fontSize:'0.68rem',letterSpacing:'1px',textTransform:'uppercase'}}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Auto-scroll review track */}
        <div className="relative overflow-hidden" style={{WebkitMaskImage:'linear-gradient(to right,transparent,black 10%,black 90%,transparent)'}}>
          <div ref={trackRef} className="flex gap-5" style={{width:'max-content',animation:'marquee-scroll 35s linear infinite'}}>
            {[...reviews, ...reviews].map((review, i) => (
              <div key={i} className="flex-shrink-0 p-7 rounded-sm border transition-all duration-300 hover:border-yellow-500/30"
                style={{width:'340px',background:'rgba(10,10,20,0.8)',border:'1px solid rgba(200,168,75,0.1)'}}>
                <div className="flex gap-0.5 mb-4">
                  {[1,2,3,4,5].map(s => <span key={s} style={{color: s <= review.rating ? '#C8A84B' : 'rgba(200,168,75,0.2)',fontSize:'0.9rem'}}>★</span>)}
                </div>
                <p className="text-sm leading-relaxed mb-5" style={{color:'rgba(212,207,197,0.65)',fontStyle:'italic',lineHeight:'1.75'}}>
                  "{review.review}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold text-black flex-shrink-0"
                    style={{background:'linear-gradient(135deg,#C8A84B,#F0D080)'}}>
                    {review.avatar}
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm">{review.name}</div>
                    <div style={{color:'#C8A84B',fontSize:'0.72rem'}}>{review.course}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Google review CTA */}
        <div className="text-center mt-12 reveal">
          <p style={{color:'rgba(212,207,197,0.5)',fontSize:'0.85rem',marginBottom:'16px'}}>
            Read more reviews on Google
          </p>
          <a href="https://g.page/its-academy-saharanpur" target="_blank" rel="noreferrer" className="btn-outline-gold text-sm tracking-widest uppercase inline-flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            View Google Reviews
          </a>
        </div>
      </div>
    </section>
  );
}
